// src/pages/billing/UsageListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingUsage, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

export const UsageListPage = observer(() => {
  const store = useBillingUsage();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    store.ListUsageRecords({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Usage Records</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Usage Records</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/billing/usage/summary"><Button variant="secondary">View Summary</Button></Link>
          <Link to="/billing/usage/new"><Button>Record Usage</Button></Link>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Usage Type</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Billed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message="No usage records found" />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || Math.random()}>
                  <TableCell>{formatDate(item.recorded_at || item.created_at)}</TableCell>
                  <TableCell>{String(item.usage_type ?? '--')}</TableCell>
                  <TableCell>{String(item.quantity ?? '--')}</TableCell>
                  <TableCell>{String(item.unit ?? '--')}</TableCell>
                  <TableCell>{formatDate(item.period_start)} - {formatDate(item.period_end)}</TableCell>
                  <TableCell>
                    {item.billed ? <Badge variant="success" size="sm">Billed</Badge> : <Badge variant="secondary" size="sm">Pending</Badge>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default UsageListPage;
